import { Component, Fragment, h, Host, Prop } from '@stencil/core';
import { appRouter } from '../../../global';

@Component({
  tag: 'entry-look-ma-no-hands',
  styleUrl: 'component.css',
  assetsDirs: ['../assets'],
  shadow: true,
})
export class EntryLookMaNoHandsPage {

  @Prop({ reflect: true }) summary: boolean = false;

  render() {
    return <Host class="article"
      {...(this.summary && appRouter.click.lookMaNoHands())}
    >
      <div class="heading content box">
        <h2>Look Ma No Hands</h2>
        <time class="aside">1 Jun 2023</time>
      </div>

      <div class="body content">
        <blockquote class="hero-background">
          <p>
            With Web APIs available now, you can do a lot without a framework.
          </p>

          <p>
            Lets have a look at some of them.
          </p>
        </blockquote>
      </div>

      {this.summary
        ? <div class="body content box">
          <noscript class="right"><a {...appRouter.href.lookMaNoHands()}>read more &hellip;</a></noscript>
        </div>
        : this.renderMore()}

    </Host>;
  }

  renderMore() {

    return <Fragment>
      <section>
        <div class="body content">
          <p>
            The initial productivity boost on using a framework to get started on your project is hard to dismiss, I want to
            get on with the logic of my application and explore the domain.
          </p>
          <p>
            Then again, revisiting an older project for bug fixes, new features and updates can be constrained: dependencies may
            conflict and even security can be compromised by an abandoned package way down deep in the dependency tree.
          </p>
          <p>
            Standards take longer to arrive and be fully supported, also, longer to be abandoned, so use them where you can.
          </p>
          <p>
            Hey, remember when jQuery was a no brainer? No? Well that was a while back now.
            It took a lot of the pain out of cross browser support, but now the browsers are much more aligned to standards
            and you don't need it.
          </p>
          <p>
            This is a well trodden debate, and there is no maxim here.
            For me, I am leaning towards less dependencies now.
          </p>
          <hr />
          <p>
            Anyway, lets pretend there's no pressure and see what we can do with standard browser APIs and some nice coding patterns.
          </p>
        </div>
      </section>

      <section>
        <div class="sub-heading content">
          <h3>
            There are some libraries that I will be using for build and test:
          </h3>
        </div>
        <div class="body content">
          <dl>
            <dt><a href="https://www.typescriptlang.org">Typescript</a></dt>
            <dd>
              <p>Type-safe coding, decorators, generics and other stuff - its a stair lift, a zimmer frame and an air fryer all in one.</p>
              <p>There is lots of <a href="https://www.typescriptlang.org">great resource</a> on why you should use it too.</p>
            </dd>
            <dt><a href="https://jestjs.io">Jest</a></dt>
            <dd>
              <p>Testing framework, Typescript support, lots and lots of documentation, walkthroughs, blogs etc.</p>
            </dd>
            <dt><a href="https://webpack.js.org">Webpack</a></dt>
            <dd>
              <p>Making my code work for test and production is easier using Webpack or Rollup.</p>
              <p>Todo: explore raw module browser support further.</p>
            </dd>
          </dl>
          <p>There is lots to discuss, and it's all going to be based around a puzzle I have written to help my son with Binary.</p>
          <p>
            You can find it here <a href="https://8bit.antix.co.uk">8bit.antix.co.uk</a>.
          </p>
          <p>
            A daily challenge to convert the 8 bit image into decimal,
            it's basic Maths, yes, but with a Computer Science flavour. - Yum :D
          </p>
        </div>
      </section>

      <section>
        <div class="sub-heading content">
          <h3>
            Next, these are the standards that I use
          </h3>
        </div>
        <div class="body content">
          <dl>
            <dt><a href="https://html.spec.whatwg.org/multipage/custom-elements.html#custom-elements">Web Components</a></dt>
            <dd>
              <p>
                <a href="https://developer.mozilla.org/en-US/docs/Web/HTML/Element/template">HTMLTemplateElement</a> and other parts
                of the Web Component spec are available on <a href="https://caniuse.com/custom-elementsv1">all major browsers</a>.
              </p>
              <p>
                Reusable, customisable Web Components are possible, and not difficult at all.
              </p>
              <p>
                Encapsulate logic, style and markup - hey, make a framework.
              </p>
            </dd>
            <dt><a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals">Template Literals</a></dt>
            <dd>
              <p>
                String interpolation in JavaScript is standardised, and
                using tag functions, you can do all sorts of composition and formatting nice an fast, in both dev and runtime.
              </p>
            </dd>
            <dt><a href="https://developer.mozilla.org/en-US/docs/Web/Manifest">Web Manifests</a> for <abbr title="Progressive Web Applications">PWAs</abbr></dt>
            <dd>
              <p>
                Gives the ability to describe your web application to the OS via the browser, and get access to even more APIs to make it act more native.
              </p>
            </dd>
            <dt><a href="https://developer.mozilla.org/en-US/docs/Web/API/Service_Worker_API">Service Workers</a></dt>
            <dd>
              <p>
                Runs on a separate browser thread, without DOM access.
              </p>
              <p>
                Allows install and caching for offline support, OS notifications, background periodic sync when online.
              </p>
              <p>
                We still need lots. In particular, proper offline <a href="https://bugs.chromium.org/p/chromium/issues/detail?id=889077">scheduled tasks</a> would be massive.
              </p>
            </dd>
          </dl>

          <p>More to come on this, in the meantime, take a look at <a href="https://developer.chrome.com/capabilities">Fugu, the Web Capabiliies Project</a>.</p>
        </div>

        <div class="body content">
          <img class="hero" src="/assets/cat-on-bike.webp" width="1024" height="492" title="AI Image. A cyberpunk brown/gold subtly striped Siberian cat white under the chin riding a chopper bike, with goggles, mischevious grin, waving his paws in the air like he just don't care, with sparks, neon, LEDs and electric cables on the bike, cloudy dramatic sky" />
        </div>
      </section>
    </Fragment>
  }
}
